import { ListboxPopover, ListboxOption, ListboxInput, ListboxArrow, ListboxButton, ListboxList, useListboxContext } from '@reach/listbox';
import { positionDefault, positionMatchWidth } from '@reach/popover';
import React__default, { forwardRef, useCallback, useState, useRef, useEffect, useMemo, Children } from 'react';
import { useTransition } from 'react-spring';
import styled from 'styled-components';
import { throttle } from 'throttle-debounce';
import { useInfiniteScroll } from '../../lib/infiniteScroll.js';
import { setTestId } from '../../lib/testId.js';
import { useTranslate } from '../../lib/translate.js';
import { useQueryChildComponents } from '../base/SystemComponent.js';
import { Button } from '../button/Button.js';
import { Chip } from '../chip/Chip.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { CSSDefaultInputField } from '../fields/common.js';
import { FIELD_ERROR_DATA_ATTRIBUTE } from '../fields/FieldError.js';
import { Icon } from '../icon/Icon.js';
import { Box } from '../layout/Box.js';
import { Inline } from '../layout/Inline.js';
import { LoadingSpinnerOverlay, LoadingSpinner } from '../loading/LoadingSpinner.js';
import { getColor } from '../styles/stylePropTypes.js';
import { Text } from '../text/Text.js';
import { CSSDefaultPopover, setReadOnlyDataAttribute, InnerInputLabel } from './InputCommon.js';

const MIN_INPUT_WIDTH_REM = 13;
/**
 * ListboxOptions don't accept an empty value, so to provide a deselect option we must
 * match on a special string.
 */ const NO_VALUE = "NO_VALUE";
const SelectPopover = styled(ListboxPopover)`
  &[data-reach-listbox-popover] {
    ${CSSDefaultPopover}
    font-size: 0.875rem;
    border-color: ${(props)=>getColor("slateGrey10", props.theme)};
    /* Ensures content does not extend past bounds of input */
    ${(props)=>props.$width === "fill" && `min-width: ${MIN_INPUT_WIDTH_REM - 1}rem;`}
  }
  &[data-reach-listbox-popover] > [data-hpy-web-select-field-listbox-list] {
    max-height: 14rem;
    overflow: auto;
  }
`;
// Using positionDefault only will result in the correct popover position but incorrect width
// Using positionMatchWidth only will result in the correct width but incorrect position (which runs off edge of browser)
// This combines the two to use the correct width and ensures the popover doesn't run off the edge of browser
const positionDefaultWithWidth = (targetRect, popoverRect)=>{
    const positionD = positionDefault(targetRect, popoverRect);
    const positionW = positionMatchWidth(targetRect, popoverRect);
    const left = parseInt(positionD.left.toString());
    const top = parseInt(positionD.top.toString());
    const width = parseInt(positionW.width.toString());
    return {
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`
    };
};
const StyledListboxOption = styled(ListboxOption)`
  &[data-reach-listbox-option] {
    height: 100%;
    min-height: 2.5rem;

    padding: 0.5rem 1rem;

    justify-content: center;
    display: flex;
    flex-direction: column;
    position: relative;

    font-size: 0.875rem;
    overflow: hidden;
    text-overflow: ellipsis;

    & > [${Text.dataId}] {
      white-space: break-spaces;
    }

    &[aria-selected='true'],
    &:hover {
      cursor: pointer;

      background: ${COLOR_PALETTE.slateGrey5};
      color: ${(props)=>getColor("context", props.theme)};
    }
  }
`;
const SelectOption = ({ label , value , testId ="X" , children , ...props })=>{
    return React__default.createElement(StyledListboxOption, {
        "data-hpy-web-select-option": true,
        ...setTestId("SelectOption-" + testId),
        ...props,
        label: label,
        value: value
    }, !!children ? children : React__default.createElement(Text, {
        weight: "semibold",
        size: "medium",
        color: "slateGreyDark60",
        align: "left"
    }, label));
};
SelectOption.dataId = "data-hpy-web-select-option";
SelectOption.displayName = "SelectOption";
const StyledSearchInput = styled.input`
  &[data-hpy-web-select-search-input] {
    position: relative;
    display: flex;
    cursor: text;
    align-items: flex-start;
    padding: 1rem 1rem 1rem 2.5rem;
    border-radius: 0;
    width: 100%;
    min-width: ${MIN_INPUT_WIDTH_REM}rem;
    height: 2.75rem;
    font-family: inherit;
    font-size: 0.875rem;
    font-weight: 500;
    border: none;
    border-bottom: 1px solid ${(props)=>getColor("slateGrey10", props.theme)};
    color: ${(props)=>getColor("slateGrey80", props.theme)};

    :focus {
      outline: none;
    }
  }
`;
const StyledIcon = styled(Icon)`
  position: absolute;
  left: 0.75rem;
  top: 0.75rem;
`;
const SelectSearchInput = ({ testId ="X" , throttleMS =600 , ...props })=>{
    const [value, setValue] = useState("");
    const t = useTranslate();
    const throttleOnChange = useCallback(// eslint-disable-next-line @typescript-eslint/no-empty-function
    throttle(throttleMS, false, props?.onChange !== undefined ? props.onChange : ()=>{}), [
        throttleMS
    ]);
    const onChange = useCallback((e)=>{
        setValue(e.target.value);
        if (props.onChange) {
            throttleOnChange(e.target.value);
        }
    }, [
        props.onChange,
        throttleOnChange
    ]);
    const ref = useRef();
    const onKeyDown = useCallback((e)=>{
        if (e.key === " ") {
            // Handle the space key locally and do not allow it to propagate to the listbox wrapper
            // Without this, the listbox will try to use the space character to "select" the current item, and will
            // block the character from appearing in the input.
            e.stopPropagation();
        }
    }, []);
    const listBox = useListboxContext();
    useEffect(()=>{
        let timer = undefined;
        if (listBox.isExpanded) {
            // Manually focus the search input on open, as reach-ui attempts to focus the first available select option
            // instead. This requires a slight delay so as to override any reach-ui focus controls.
            timer = setTimeout(()=>{
                ref?.current?.focus();
            }, 100);
        } else {
            // Select popover has been closed, clear the search value
            setValue("");
            if (props.onChange) {
                props.onChange("");
            }
        }
        return ()=>{
            if (timer !== undefined) {
                clearTimeout(timer);
            }
        };
    }, [
        listBox.isExpanded
    ]);
    return React__default.createElement(Box, null, React__default.createElement(StyledSearchInput, {
        "data-hpy-web-select-search-input": true,
        ...props,
        ...setTestId(`SelectInput-Search-${testId}`),
        ref: ref,
        placeholder: t("system:SelectInput.search"),
        value: value,
        onChange: onChange,
        onKeyDown: onKeyDown
    }), React__default.createElement(StyledIcon, {
        icon: "search",
        size: "xsmall"
    }));
};
SelectSearchInput.dataId = "data-hpy-web-select-search-input";
SelectSearchInput.displayName = "SelectSearchInput";
const StyledListboxInput = styled(ListboxInput)`
  &[data-hpy-web-select-field-input-base] [data-reach-listbox-button] {
    ${CSSDefaultInputField}
    position: relative;
    display: flex;
    cursor: pointer;
    padding: 1.375rem 2.5rem 0.5rem 1rem;
    align-items: flex-start;
    min-width: ${MIN_INPUT_WIDTH_REM}rem;
    min-height: 3rem;

    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &,
    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &:hover,
    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &:focus {
      color: inherit;
    }
  }
  &[data-hpy-web-select-field-input-chip] [data-reach-listbox-button] {
    min-width: ${MIN_INPUT_WIDTH_REM}rem;
  }

  &[data-hpy-web-select-field-input-base][data-state='expanded'] [data-reach-listbox-button],
  &[data-hpy-web-select-field-input-base][data-state='expanded'] [data-reach-listbox-button]:hover,
  &[data-hpy-web-select-field-input-base] [data-reach-listbox-button]:focus {
    outline: none;
    border-color: ${(props)=>getColor("context", props.theme)};
  }
`;
const StyledListBoxArrow = styled(ListboxArrow)`
  position: absolute;
  top: 50%;
  margin-top: -2px;
  right: 1rem;
  display: block;
  width: 0px;
  height: 0px;
  content: '';
  transform: rotate(0deg);
  border-top: 5px solid ${COLOR_PALETTE.slateGrey100};
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  transition: transform 0.2s ease 0s, -webkit-transform 0.2s ease 0s;

  &[data-expanded] {
    transform: rotate(180deg);
  }
`;
const StyledNoResults = styled(Box)`
  height: 2.5rem;
  padding: 0 1rem;
  justify-content: center;
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
  color: ${COLOR_PALETTE.slateGrey40};
`;
const SelectedText = styled(Text)`
  font-size: 0.875rem;
`;
const StyledLoadingSpinnerOverlay = styled(LoadingSpinnerOverlay)`
  margin-top: 2.75rem;
  padding-bottom: 2.75rem;
`;
const useSelectedLabel = (children, value)=>{
    const selectedLabel = useRef();
    return useMemo(()=>{
        Children.forEach(children, (child)=>{
            if (child && child.props.value === value) {
                selectedLabel.current = child.props.label;
            }
        });
        return selectedLabel.current;
    }, [
        children,
        value
    ]);
};
const isBase = (props)=>props?.as === undefined;
const isChip = (props)=>props?.as === "chip";
const isButton = (props)=>props?.as === "button";
const SelectInputAs = forwardRef(({ children , label , width ="fill" , readOnly =false , testId ="X" , throttleMS =600 , value =NO_VALUE , loading =false , loadingMore =false , hasNextPage =false , onLoadMore =undefined , onSearch , onFocus , onOpen , ...props }, ref)=>{
    const selectedLabel = useSelectedLabel(children, value);
    const t = useTranslate();
    let asProps = {};
    let asComp = undefined;
    if (isChip(props)) {
        asProps = {
            testId: `SelectInput-${testId}`,
            label,
            sublabel: selectedLabel,
            icon: props.icon
        };
        asComp = Chip;
    } else if (isButton(props)) {
        asProps = {
            testId: `SelectInput-${testId}`,
            icon: props.icon,
            variant: props.variant
        };
        asComp = Button;
    }
    const [options, others] = useQueryChildComponents(children, [
        SelectOption
    ]);
    const scroll = useInfiniteScroll({
        loading: loadingMore,
        hasNextPage,
        onLoadMore: onLoadMore ?? (()=>null),
        // Arbitrary margin so that items are loaded before 'bottom' of scroll is reached
        containerMargin: "0px 0px 150px 0px"
    });
    // Delay the loading exit, so that the Select dropdown has enough time to rerender before 'loading' is complete
    const transition = useTransition(loading, {
        from: {
            opacity: 0
        },
        enter: {
            opacity: 1
        },
        leave: {
            opacity: 0
        },
        trail: 100
    });
    const handleOnKeyDown = useCallback((event)=>{
        // Capture focus + space key keyboard usage of select input
        if (event.key === " " && onOpen !== undefined) {
            onOpen();
        }
    }, [
        onOpen
    ]);
    return React__default.createElement(StyledListboxInput, {
        ref: ref,
        "data-hpy-web-select-input": true,
        ...isBase(props) ? {
            "data-hpy-web-select-field-input-base": true
        } : {},
        ...isChip(props) ? {
            "data-hpy-web-select-field-input-chip": true
        } : {},
        ...setTestId("SelectInput-" + testId),
        ...props,
        value: value,
        as: undefined,
        onFocus: onFocus
    }, (listbox)=>React__default.createElement(React__default.Fragment, null, React__default.createElement(ListboxButton, {
            ...selectedLabel ? {
                "data-hpy-web-select-input-input-has-value": true
            } : {},
            ...asProps,
            ...setTestId("SelectInput-Button-" + testId),
            disabled: readOnly,
            ...setReadOnlyDataAttribute(readOnly),
            as: asComp,
            onClick: onOpen,
            onKeyDown: handleOnKeyDown
        }, isBase(props) && React__default.createElement(React__default.Fragment, null, React__default.createElement(SelectedText, {
            truncate: true,
            align: "left"
        }, selectedLabel), React__default.createElement(StyledListBoxArrow, null, "\xa0"), React__default.createElement(InnerInputLabel, {
            "data-hpy-web-select-input-input-inner-label": true,
            size: "medium",
            weight: "semibold"
        }, label)), isButton(props) && (props?.showSelectedLabel && !!selectedLabel ? selectedLabel : label), !isBase(props) && others), React__default.createElement(SelectPopover, {
            ...setTestId("Popover-" + testId),
            position: positionDefaultWithWidth,
            portal: true,
            "$width": width
        }, typeof onSearch === "function" && React__default.createElement(SelectSearchInput, {
            testId: testId,
            throttleMS: throttleMS,
            onChange: onSearch
        }), React__default.createElement(ListboxList, {
            ref: scroll.containerRef,
            "data-hpy-web-select-field-listbox-list": true
        }, !listbox.isExpanded || options === undefined || options === null || options.length === 0 ? React__default.createElement(StyledNoResults, {
            ...setTestId("SelectInput-NoResults-" + testId)
        }, t("system:SelectInput.no_options")) : options, hasNextPage && React__default.createElement(Box, {
            ref: scroll.scrollElementRef,
            width: "fill",
            minHeight: 3,
            as: "li"
        }, React__default.createElement(Inline, {
            align: "center",
            alignY: "center",
            width: "fill"
        }, loadingMore && React__default.createElement(LoadingSpinner, {
            size: "small",
            testId: "SelectInput-LoadMore-" + testId
        })))), transition((_, show)=>show && React__default.createElement(Box, null, React__default.createElement(StyledLoadingSpinnerOverlay, {
                size: "medium",
                bgOverlay: "partial",
                testId: "SelectInput-Load-" + testId
            }))))));
});
SelectInputAs.dataId = "data-hpy-web-select-input-as";
SelectInputAs.displayName = "SelectInputAs";
/**
 * Select Input.
 * Accepts `SelectOptions` as child components. If a 'deselected' option is required, it must be manually added to
 * options with a value of `NO_VALUE`.
 *
 * @example
 * <SelectInput name="chicken">
 *  <SelectOption label="" value={NO_VALUE} />
 *  <SelectOption label="Egg" value="egg" />
 *  <SelectOption label="Duck" value="duck" />
 * </SelectInput>
 */ const SelectInput = forwardRef(({ children , ...props }, ref)=>{
    if (isBase(props)) {
        return React__default.createElement(SelectInputAs, {
            ...props,
            ref: ref
        }, children);
    }
    if (isChip(props)) {
        return React__default.createElement(SelectInputAs, {
            ...props,
            ref: ref
        }, children);
    }
    if (isButton(props)) {
        return React__default.createElement(SelectInputAs, {
            ...props,
            ref: ref
        }, children);
    }
    return null;
});
SelectInput.dataId = "data-hpy-web-select-input";
SelectInput.displayName = "SelectInput";

export { NO_VALUE, SelectInput, SelectOption };
